import React, { FC, ReactElement, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import FormRow from '../../Common/Components/FormRow/FormRow'
import { ValidationErrors } from '../../Common/Components/ValidationErrors/ValidationErrors'
import {
    CALL_MODEL_DIAG_DUMP_ENABLED_FOR_DEV_FIELD_NAME,
    COMMS_ENABLED_FIELD_NAME,
    CTI_CALL_MODEL_LOG_ENABLED_FIELD_NAME,
    CTI_LOG_ENABLED_FIELD_NAME,
    CTI_LOG_INC_CLASS_NAMES_FIELD_NAME,
    EXTENSIONS_PER_ENTERPRISE_USER_FIELD_NAME,
    IN_SERVICE_FIELD_NAME,
    INTERNAL_DIAG_DUMP_ENABLED_FIELD_NAME,
    MAX_MONITORED_USERS_FIELD_NAME,
    MAX_USER_SYNC_FIELD_NAME,
    PARTITION_COUNT_MAX_FIELD_NAME,
    PARTITIONS_RPTING_SCOPE_ALL_ENABLED_FIELD_NAME,
    SCHEDULED_EMAILS_ALLOWED_PER_REPORT_NAME,
    SCHEDULED_REPORTS_ALLOWED_PER_USER_NAME,
    TYPE_FIELD_NAME,
    USER_NOTIFICATIONS_ENABLED_FIELD_NAME
} from '../Constants/FieldNames'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { LanguageContext, ReactState, UserInfoContext } from '../../Common/Contexts'
import { RulesLanguage, TelSysConfiguration } from '../../../../i18n'
import { useFormContext, UseFormMethods, ValidateResult } from '../../../Vendor/react-hook-form/dist/index.ie11'
import { TelSysForm } from '../Types/Types'
import {
    DEFAULT_MAX_ALLOWED_PARTITIONS_COUNT,
    DEFAULT_MAX_USERS_MONITORED,
    DEFAULT_MAX_USERS_SYNC,
    MAX_DEFAULT_SCHEDULED_REPORTS_ALLOWED_PER_USER,
    MAX_DEFAULT_SCHEDULED_EMAILS_ALLOWED_PER_REPORT,
    PARTITION_COUNT_MAX_UNLOCK_HOTKEY,
    TEL_SYS_TYPE_VALUE_MAP,
    TelSysTypes
} from '../Constants/Constants'
import { getMaxAllowedPartitionsCount } from '../Functions/PartitionLimits'
import { Rules } from '../../Common/Rules/Rules'
import { isStringNotEmpty, isVarNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { UserInfoContextType } from '../../Common/Contexts/UserInfoContext'
import { ALL_VALUE } from '../../Common/API/ItemsGetters'
import PromptContext, { PromptDisplayType } from '../../Common/Contexts/PromptContext'
import { displayError } from '../../Common/Utils/Messages'
import { PermissionsUtil } from '../../Common/Utils/PermissionsUtil'
import { AdminBackendPermissionNames } from '../../Common/Constants/Permissions'
import { requestJSON } from '../../../NonReact/common/API/REST/RESTClient'
import { telSysCallCaptureControlURL } from '../../../NonReact/constants/ApiConstants'
import { RESTMethods } from '../../../NonReact/constants/REST'
import { ProcessedTelSysProfile, Rules as FieldValidationRules } from '../../Common/Types/Types'
import { getTelSysProfiles } from '../../Common/API/Licence/LicenceAPI'
import { OutlinedTextInput } from '../../../ReactRouting/Components/Atoms/Inputs/OutlinedTextInput'
import { RouterCheckbox } from '../../../ReactRouting/Components/Atoms/Inputs/RouterCheckbox'
import { RoundedButton } from '../../../ReactRouting/Components/Atoms/RoundedButton/RoundedButton'

type Props = {
    isEditMode?: boolean
    selectedTelSysID?: string
    viewOnly?: boolean
    voiceAppBotsDisabled?: Nullable<boolean>
    getTelephoneServer?: () => void
    availableTelSysProfiles: Nullable<ProcessedTelSysProfile[]>
    hasTelsysSyncRestrictionPerms: boolean
}

export const AdvancedTab: FC<Props> = ({
    viewOnly,
    isEditMode,
    selectedTelSysID,
    voiceAppBotsDisabled,
    getTelephoneServer,
    availableTelSysProfiles,
    hasTelsysSyncRestrictionPerms
}: Props): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler

    const userInfo: UserInfoContextType = useContext(UserInfoContext)
    const { displayPrompt }: PromptDisplayType = useContext(PromptContext)

    const { watch, setValue, trigger, getValues }: UseFormMethods<TelSysForm> = useFormContext()

    const telSysType: TelSysTypes = watch(TYPE_FIELD_NAME)

    const isTelSysUnknown: boolean = useMemo((): boolean => telSysType === TelSysTypes.UNKNOWN, [telSysType])
    const isMsTeamsTelSys: boolean = useMemo((): boolean => telSysType === TelSysTypes.MS_TEAMS, [telSysType])

    const [
        shouldShowScheduledReportsEmailsMaxValues,
        setShouldShowScheduledReportsEmailsMaxValues
    ]: ReactState<boolean> = useState<boolean>(false)

    const [maxAllowedPartitionsCount, setMaxAllowedPartitionsCount]: ReactState<number> = useState<number>(
        DEFAULT_MAX_ALLOWED_PARTITIONS_COUNT
    )
    const [isPartitionCountMaxUnlocked, setIsPartitionCountMaxUnlocked]: ReactState<boolean> = useState<boolean>(false)

    const isEnterpriseAvailable: boolean = useMemo(
        (): boolean =>
            telSysType === TelSysTypes.BROADSOFT_BROADWORKS ||
            telSysType === TelSysTypes.WEBEX_CALLING ||
            telSysType === TelSysTypes.MS_TEAMS,
        [telSysType]
    )

    const commsEnabledValue: boolean = watch(COMMS_ENABLED_FIELD_NAME)
    const inServiceValue: boolean = watch(IN_SERVICE_FIELD_NAME)

    const isVoiceBotsButtonVisible: boolean = useMemo(
        (): boolean =>
            !!(
                isMsTeamsTelSys &&
                isEditMode &&
                commsEnabledValue &&
                inServiceValue &&
                isVarNotEmpty(voiceAppBotsDisabled) &&
                userInfo?.Scope?.TelSysID?.[0] === ALL_VALUE &&
                PermissionsUtil.hasPermissions(
                    userInfo,
                    PermissionsUtil.builder(AdminBackendPermissionNames.TEL_SYS).update().read()
                )
            ),
        [isMsTeamsTelSys, isEditMode, commsEnabledValue, inServiceValue, voiceAppBotsDisabled, userInfo]
    )

    const clickVoiceAppBotsButton = useCallback((): void => {
        displayPrompt(
            language.get(TelSysConfiguration.TelSysConfiguration_EnableVoiceAppBots_Confirmation_Title),
            language.get(TelSysConfiguration.TelSysConfiguration_EnableVoiceAppBots_Confirmation_Yes),
            language.get(TelSysConfiguration.TelSysConfiguration_EnableVoiceAppBots_Confirmation_No),
            (): void => {
                if (isStringNotEmpty(selectedTelSysID)) {
                    requestJSON(telSysCallCaptureControlURL(selectedTelSysID), {
                        method: RESTMethods.PUT,
                        body: { BotsDisabled: !voiceAppBotsDisabled }
                    })
                        .then((): void => {
                            setTimeout((): void => getTelephoneServer?.())
                        })
                        .catch(displayError)
                }
            }
        )
    }, [voiceAppBotsDisabled])

    const isPartitionCountMaxDisabled: boolean = !!viewOnly || (isMsTeamsTelSys && !isPartitionCountMaxUnlocked)

    const partitionCountMaxRules: FieldValidationRules = useMemo(
        (): FieldValidationRules => ({
            required: language.get(TelSysConfiguration.TelSysConfiguration_Error_Required),
            validate: {
                isNumber: Rules.isNumber(language),
                ...(isMsTeamsTelSys
                    ? {
                          isUnderLimit: Rules.isUnderLimit(
                              language,
                              TelSysConfiguration.TelSysConfiguration_Error_MaxAllowedPartitionsExceeded,
                              maxAllowedPartitionsCount,
                              false
                          )
                      }
                    : {})
            }
        }),
        [language, isMsTeamsTelSys, maxAllowedPartitionsCount]
    )

    const filteredTelSysProfile: Nullable<ProcessedTelSysProfile> = useMemo(
        (): Nullable<ProcessedTelSysProfile> =>
            availableTelSysProfiles?.find(
                (telSysProfile: ProcessedTelSysProfile): boolean =>
                    telSysProfile?.type === TEL_SYS_TYPE_VALUE_MAP[telSysType]
            ),
        [availableTelSysProfiles, telSysType]
    )

    useHotkeys(
        'shift+alt+e',
        (event: Event): void => {
            event?.preventDefault()
            setShouldShowScheduledReportsEmailsMaxValues((prev: boolean): boolean => !prev)
        },
        {
            enableOnFormTags: true
        }
    )

    useHotkeys(
        PARTITION_COUNT_MAX_UNLOCK_HOTKEY,
        (event: Event): void => {
            event?.preventDefault()
            setIsPartitionCountMaxUnlocked((prev: boolean): boolean => !prev)
        },
        {
            enableOnFormTags: true,
            enabled: isMsTeamsTelSys && !viewOnly
        }
    )

    useEffect((): void => {
        if (!isMsTeamsTelSys) return

        getTelSysProfiles()
            .then((telSysProfiles: Nullable<ProcessedTelSysProfile[]>): void =>
                setMaxAllowedPartitionsCount(getMaxAllowedPartitionsCount(telSysProfiles))
            )
            .catch(displayError)
    }, [isMsTeamsTelSys])

    useEffect((): void => {
        if (!isMsTeamsTelSys || isEditMode) return

        const partitionCountMaxCurrent: number = Number(getValues(PARTITION_COUNT_MAX_FIELD_NAME))
        const hasUsableCurrentValue: boolean = Number.isFinite(partitionCountMaxCurrent) && partitionCountMaxCurrent > 0

        setValue(
            PARTITION_COUNT_MAX_FIELD_NAME,
            hasUsableCurrentValue
                ? Math.min(partitionCountMaxCurrent, maxAllowedPartitionsCount)
                : maxAllowedPartitionsCount
        )

        setTimeout((): void => {
            trigger(PARTITION_COUNT_MAX_FIELD_NAME)
        })
    }, [isMsTeamsTelSys, isEditMode, maxAllowedPartitionsCount])

    useEffect((): void => {
        if (isVarNotEmpty(filteredTelSysProfile) && isMsTeamsTelSys && !isEditMode) {
            setValue(
                MAX_USER_SYNC_FIELD_NAME,
                filteredTelSysProfile?.LicenseValues?.MaximumAllowedUsersSync ?? DEFAULT_MAX_USERS_SYNC
            )
            setValue(
                MAX_MONITORED_USERS_FIELD_NAME,
                filteredTelSysProfile?.LicenseValues?.MaximumAllowedUsersMonitored ?? DEFAULT_MAX_USERS_MONITORED
            )

            setTimeout((): void => {
                trigger(MAX_USER_SYNC_FIELD_NAME)
                trigger(MAX_MONITORED_USERS_FIELD_NAME)
            })
        }
    }, [filteredTelSysProfile, isMsTeamsTelSys, isEditMode])

    return (
        <div className='telsys-advanced-tab tab-area'>
            <FormRow>
                <ValidationErrors name={PARTITION_COUNT_MAX_FIELD_NAME}>
                    <OutlinedTextInput
                        inputName={PARTITION_COUNT_MAX_FIELD_NAME}
                        labelLeft={language.get(TelSysConfiguration.TelSysConfiguration_Field_PartitionCountMax_Label)}
                        disabled={isPartitionCountMaxDisabled}
                        rules={partitionCountMaxRules}
                    />
                </ValidationErrors>
            </FormRow>
            {shouldShowScheduledReportsEmailsMaxValues ? (
                <>
                    <FormRow>
                        <ValidationErrors name={SCHEDULED_REPORTS_ALLOWED_PER_USER_NAME}>
                            <OutlinedTextInput
                                inputName={SCHEDULED_REPORTS_ALLOWED_PER_USER_NAME}
                                labelLeft={language.get(
                                    TelSysConfiguration.TelSysConfiguration_Field_ScheduledReportsPerUser_Label
                                )}
                                disabled={viewOnly}
                                rules={{
                                    validate: {
                                        isUnderLimit: Rules.isUnderLimit(
                                            language,
                                            RulesLanguage.Rules_Validation_MustBeUnderLimit,
                                            MAX_DEFAULT_SCHEDULED_REPORTS_ALLOWED_PER_USER,
                                            false
                                        ),
                                        isPositive: Rules.isPositiveNumber(language)
                                    }
                                }}
                            />
                        </ValidationErrors>
                    </FormRow>
                    <FormRow>
                        <ValidationErrors name={SCHEDULED_EMAILS_ALLOWED_PER_REPORT_NAME}>
                            <OutlinedTextInput
                                inputName={SCHEDULED_EMAILS_ALLOWED_PER_REPORT_NAME}
                                labelLeft={language.get(
                                    TelSysConfiguration.TelSysConfiguration_Field_EmailsPerScheduledReport_Label
                                )}
                                disabled={viewOnly}
                                rules={{
                                    validate: {
                                        isUnderLimit: Rules.isUnderLimit(
                                            language,
                                            RulesLanguage.Rules_Validation_MustBeUnderLimit,
                                            MAX_DEFAULT_SCHEDULED_EMAILS_ALLOWED_PER_REPORT,
                                            false
                                        ),
                                        isPositive: Rules.isPositiveNumber(language)
                                    }
                                }}
                            />
                        </ValidationErrors>
                    </FormRow>
                </>
            ) : (
                <></>
            )}
            {isMsTeamsTelSys ? (
                <>
                    <FormRow>
                        <ValidationErrors name={MAX_USER_SYNC_FIELD_NAME}>
                            <OutlinedTextInput
                                inputName={MAX_USER_SYNC_FIELD_NAME}
                                labelLeft={language.get(
                                    TelSysConfiguration.TelSysConfiguration_Field_Max_Allowed_User_Sync_Label
                                )}
                                disabled={viewOnly || !hasTelsysSyncRestrictionPerms}
                                rules={{
                                    required: language.get(TelSysConfiguration.TelSysConfiguration_Error_Required),
                                    validate: {
                                        isNumber: Rules.isNumber(language),
                                        isPositiveNumber: Rules.isPositiveNumber(language)
                                    }
                                }}
                            />
                        </ValidationErrors>
                    </FormRow>
                    <FormRow>
                        <ValidationErrors name={MAX_MONITORED_USERS_FIELD_NAME}>
                            <OutlinedTextInput
                                inputName={MAX_MONITORED_USERS_FIELD_NAME}
                                labelLeft={language.get(
                                    TelSysConfiguration.TelSysConfiguration_Field_Max_Allowed_Monitored_Users_Label
                                )}
                                disabled={viewOnly || !hasTelsysSyncRestrictionPerms}
                                rules={{
                                    required: language.get(TelSysConfiguration.TelSysConfiguration_Error_Required),
                                    validate: {
                                        isNumber: Rules.isNumber(language),
                                        isPositiveNumber: Rules.isPositiveNumber(language)
                                    }
                                }}
                            />
                        </ValidationErrors>
                    </FormRow>
                </>
            ) : (
                <></>
            )}

            <FormRow>
                <RouterCheckbox
                    name={PARTITIONS_RPTING_SCOPE_ALL_ENABLED_FIELD_NAME}
                    labelLeft={language.get(
                        TelSysConfiguration.TelSysConfiguration_Field_PartitionsRptingScopeAllEnabled_Label
                    )}
                    disabled={viewOnly}
                />
            </FormRow>
            {isEnterpriseAvailable ? (
                <FormRow>
                    <ValidationErrors name={EXTENSIONS_PER_ENTERPRISE_USER_FIELD_NAME}>
                        <OutlinedTextInput
                            inputName={EXTENSIONS_PER_ENTERPRISE_USER_FIELD_NAME}
                            labelLeft={language.get(
                                TelSysConfiguration.TelSysConfiguration_Field_ExtensionsPerEnterpriseUser_Label
                            )}
                            disabled={viewOnly}
                            rules={{
                                validate: {
                                    isNumber: (value: string): ValidateResult => {
                                        if (value && isNaN(Number(value))) {
                                            return language.get(
                                                TelSysConfiguration.TelSysConfiguration_Error_MustBeNumber
                                            )
                                        }

                                        return undefined
                                    }
                                }
                            }}
                        />
                    </ValidationErrors>
                </FormRow>
            ) : (
                <></>
            )}
            <FormRow>
                <RouterCheckbox
                    name={CTI_LOG_ENABLED_FIELD_NAME}
                    labelLeft={language.get(TelSysConfiguration.TelSysConfiguration_Field_CTILogEnabled_Label)}
                    disabled={viewOnly}
                />
            </FormRow>
            <FormRow>
                <RouterCheckbox
                    name={CTI_CALL_MODEL_LOG_ENABLED_FIELD_NAME}
                    labelLeft={language.get(TelSysConfiguration.TelSysConfiguration_Field_CTICallModelLogEnabled_Label)}
                    disabled={viewOnly}
                />
            </FormRow>
            <FormRow>
                <RouterCheckbox
                    name={CTI_LOG_INC_CLASS_NAMES_FIELD_NAME}
                    labelLeft={language.get(TelSysConfiguration.TelSysConfiguration_Field_CTILogIncClassNames_Label)}
                    disabled={viewOnly}
                />
            </FormRow>
            <FormRow>
                <OutlinedTextInput
                    inputName={CALL_MODEL_DIAG_DUMP_ENABLED_FOR_DEV_FIELD_NAME}
                    labelLeft={language.get(
                        TelSysConfiguration.TelSysConfiguration_Field_CallModelDiagDumpEnabledForDev_Label
                    )}
                    disabled={viewOnly}
                />
            </FormRow>
            {!isTelSysUnknown ? (
                <FormRow>
                    <RouterCheckbox
                        name={INTERNAL_DIAG_DUMP_ENABLED_FIELD_NAME}
                        labelLeft={language.get(
                            TelSysConfiguration.TelSysConfiguration_Field_InternalDiagDumpEnabled_Label
                        )}
                        disabled={viewOnly}
                    />
                </FormRow>
            ) : (
                <></>
            )}
            {!isTelSysUnknown ? (
                <FormRow>
                    <RouterCheckbox
                        name={USER_NOTIFICATIONS_ENABLED_FIELD_NAME}
                        labelLeft={language.get(
                            TelSysConfiguration.TelSysConfiguration_Field_UserNotificationsEnabled_Label
                        )}
                        disabled={viewOnly}
                    />
                </FormRow>
            ) : (
                <></>
            )}
            {isVoiceBotsButtonVisible ? (
                <FormRow>
                    <RoundedButton isOutlined type='button' onClick={clickVoiceAppBotsButton}>
                        {voiceAppBotsDisabled
                            ? language.get(TelSysConfiguration.TelSysConfiguration_EnableVoiceAppBots)
                            : language.get(TelSysConfiguration.TelSysConfiguration_DisableVoiceAppBots)}
                    </RoundedButton>
                </FormRow>
            ) : (
                <></>
            )}
        </div>
    )
}
